import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { RefreshRounded, ToggleOffOutlined } from '@mui/icons-material';
import { getAiAccessSettings } from '../../../../helpers/api';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import { BRAND, isSetupMissingError } from './constants';

function SetupNotice({ onRetry, checking }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 5 },
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(12, 91, 171, 0.08)',
          color: BRAND,
        }}
      >
        <ToggleOffOutlined sx={{ fontSize: 30 }} />
      </Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        AI access isn't set up on the server yet
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
        Until it is, the AI assistant and AI reports stay available to everyone with no daily limits.
        Ask your backend team to run the AI access setup script (<code>Scripts/aiaccess.sql</code>) on
        the database, then check again.
      </Typography>
      <Button
        variant="outlined"
        startIcon={checking ? <CircularProgress size={16} /> : <RefreshRounded />}
        disabled={checking}
        onClick={onRetry}
        sx={{ mt: 3, borderRadius: '8px' }}
      >
        Check again
      </Button>
    </Paper>
  );
}

// Checks once that the AI access tables exist on the server, then shows the page
function useAiAccessSetup() {
  const [setup, setSetup] = useState('checking'); // 'checking' | 'ready' | 'missing'
  const [rechecking, setRechecking] = useState(false);

  const checkSetup = useCallback(async () => {
    try {
      // The shared (ALL) setting exists once the server's AI access setup has been run
      await getAiAccessSettings({ feature: 'ALL', scope: 'GLOBAL' });
      setSetup('ready');
    } catch (error) {
      // Other errors are shown by the pages themselves
      setSetup(isSetupMissingError(error) ? 'missing' : 'ready');
    }
  }, []);

  useEffect(() => {
    checkSetup();
  }, [checkSetup]);

  const recheck = async () => {
    setRechecking(true);
    await checkSetup();
    setRechecking(false);
  };

  return { setup, rechecking, recheck };
}

function AiAccessPage({ title, description, children }) {
  const { setup, rechecking, recheck } = useAiAccessSetup();

  return (
    <Box>
      <Box sx={{ pb: 2.5, mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 720 }}>
          {description}
        </Typography>
      </Box>
      {setup === 'checking' ? (
        <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: '16px' }}>
          <LoaderSkeleton />
        </Paper>
      ) : setup === 'missing' ? (
        <SetupNotice onRetry={recheck} checking={rechecking} />
      ) : (
        children
      )}
    </Box>
  );
}

export default AiAccessPage;
